import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  InputAdornment
} from '@mui/material';
import { Search as SearchIcon, Edit as EditIcon } from '@mui/icons-material';
import StatusBadge from '../crm/StatusBadge';
import { getReps } from '../../services/mock/crmService';
import { assignCompanyRep } from '../../services/mock/companyService';

const CompanyHeader = ({ company, org, onRepChange }) => {
  const [repModalOpen, setRepModalOpen] = useState(false);
  const [reps, setReps] = useState([]);
  const [repSearch, setRepSearch] = useState('');

  const showGMV = process.env.REACT_APP_SHOW_GMV === 'true';

  const handleOpenRepModal = async () => {
    const repsData = await getReps(org);
    setReps(repsData);
    setRepModalOpen(true);
  };

  const handleAssignRep = async (repId) => {
    const selectedRep = reps.find(r => r.id === repId);
    await assignCompanyRep(org, company.id, repId);
    onRepChange(selectedRep);
    setRepModalOpen(false);
  };

  const formatCurrency = (value) => `$${value?.toLocaleString() || 0}`;

  const filteredReps = reps.filter(rep => 
    rep.name.toLowerCase().includes(repSearch.toLowerCase()) ||
    rep.territory.toLowerCase().includes(repSearch.toLowerCase())
  );

  return (
    <>
      <Box sx={{ mb: 4 }}>
        {/* Company Name and Location */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
            {company.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#64748b' }}>
              {company.city && company.state ? `${company.city}, ${company.state}` : 'Location not specified'}
            </Typography>
            {company.industry && (
              <>
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#cbd5e1' }} />
                <Typography variant="body1" sx={{ color: '#64748b' }}>
                  {company.industry}
                </Typography>
              </>
            )}
          </Box>
        </Box>

        {/* Badges and Rep Assignment */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <StatusBadge status={company.status} />
          {company.tier && <StatusBadge status={company.tier} />}
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Assigned Rep:
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleOpenRepModal}
              startIcon={<EditIcon />}
              sx={{ borderColor: '#d1d5db', color: '#374151' }}
            >
              {company.repName || 'Unassigned'}
            </Button>
          </Box>
        </Box>

        {/* Quick Stats */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {showGMV && (
            <Chip
              label={`Revenue: ${formatCurrency(company.revenue12m)}`}
              sx={{
                bgcolor: '#f0fdf4',
                color: '#166534',
                fontWeight: 600,
                '& .MuiChip-label': { px: 2 }
              }}
            />
          )}
          <Chip
            label={`Orders: ${company.orders12m}`}
            sx={{
              bgcolor: '#eff6ff',
              color: '#1d4ed8',
              fontWeight: 600,
              '& .MuiChip-label': { px: 2 }
            }}
          />
          <Chip
            label={`Clinics: ${company.clinics}`}
            sx={{
              bgcolor: '#fef3c7',
              color: '#d97706',
              fontWeight: 600,
              '& .MuiChip-label': { px: 2 }
            }}
          />
          <Chip
            label={`Users: ${company.users}`}
            sx={{
              bgcolor: '#f3e8ff',
              color: '#7c3aed',
              fontWeight: 600,
              '& .MuiChip-label': { px: 2 }
            }}
          />
          {showGMV && company.aov12m && (
            <Chip
              label={`AOV: ${formatCurrency(company.aov12m)}`}
              sx={{
                bgcolor: '#ecfdf5',
                color: '#059669',
                fontWeight: 600,
                '& .MuiChip-label': { px: 2 }
              }}
            />
          )}
        </Box>
      </Box>

      {/* Rep Assignment Modal */}
      <Dialog open={repModalOpen} onClose={() => setRepModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Sales Rep</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search reps..."
            value={repSearch}
            onChange={(e) => setRepSearch(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          <List sx={{ maxHeight: 300, overflowY: 'auto' }}>
            {filteredReps.map(rep => (
              <ListItem key={rep.id} disablePadding>
                <ListItemButton onClick={() => handleAssignRep(rep.id)}>
                  <ListItemText
                    primary={rep.name}
                    secondary={`${rep.territory} • ${rep.email}`}
                  />
                  {!rep.active && (
                    <Chip label="Inactive" size="small" color="warning" />
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CompanyHeader;
